import axios from 'axios';
import config from '../config';

const api = axios.create({
  baseURL: config.API_URL,
  timeout: 30000,
});

// Interceptor para adicionar token de autenticação se necessário
api.interceptors.request.use(
  (config) => {
    // Adicionar token se existir
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de resposta
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Só redireciona se não estiver na página de login
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      // Token expirado ou inválido - só redireciona se não estiver no login
      localStorage.removeItem('token');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      // Acesso negado (não é admin, etc.)
      console.error('Acesso negado:', error.response?.data?.detail);
      // Não redirecionar, apenas mostrar erro
    }
    return Promise.reject(error);
  }
);

// Funções específicas da API
export const processarArquivo = () => api.post('/processar-arquivo', null, { timeout: 30000 });
export const obterConfiguracoes = () => api.get('/configuracoes');
export const agendarProcessamento = (horario) => api.post('/agendar-processamento', { horario });
export const testarFtp = () => api.post('/testar-ftp');
export const listarArquivosFtp = () => api.get('/arquivos-ftp');
export const obterHistorico = () => api.get('/historico');

// Novas funções
export const processarManual = (dadosJson) =>
  api.post('/processar-manual', { dados_json: dadosJson }, { timeout: 30000 });
export const processarExcel = (file, templateId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  if (templateId) {
    formData.append('template_id', templateId);
  }
  return api.post('/processar-excel', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 30000, // 30 segundos - apenas para iniciar o processamento
  });
};

export const obterStatusProcessamento = (jobId) => {
  return api.get(`/processar-excel/status/${jobId}`);
};

export const listarJobsProcessamento = (limit = 50, offset = 0) => {
  return api.get(`/processar-excel/jobs?limit=${limit}&offset=${offset}`);
};
export const downloadExemploExcel = () => {
  return api.get('/download-exemplo-excel', {
    responseType: 'blob',
  });
};
export const toggleAgendamento = () => api.post('/toggle-agendamento');
export const pararAgendamento = () => api.post('/parar-agendamento');
export const getHistoricoEnvios = (page = 1, limit = 20, search = null, status = null) => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  if (search) params.append('search', search);
  if (status) params.append('status', status);
  
  return api.get(`/historico-envios?${params.toString()}`);
};

// Funções para o sistema de atendimento
export const getChats = async (departamentoId = 10, finalizada = false) => {
  try {
    const response = await api.get(`/chats?departamento_id=${departamentoId}&finalizada=${finalizada}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar chats:', error);
    throw error;
  }
};

export const getChatMessages = async (chatId) => {
  try {
    const response = await api.get(`/chats/${chatId}/messages`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar mensagens do chat:', error);
    throw error;
  }
};

export const markChatAsRead = async (chatId, lastMessageId) => {
  try {
    const response = await api.post(`/chats/${chatId}/read`, {
      last_message_id: lastMessageId || 0,
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao marcar chat como lido:', error);
    throw error;
  }
};

export const sendMessage = async (chatId, content) => {
  try {
    const response = await api.post(`/chats/${chatId}/messages`, null, {
      params: { content }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao enviar mensagem:', error);
    throw error;
  }
};

export const finalizarChat = async (chatId) => {
  try {
    const response = await api.patch(`/chats/${chatId}/finalizar`);
    return response.data;
  } catch (error) {
    console.error('Erro ao finalizar chat:', error);
    throw error;
  }
};

export const uploadFiles = async (chatId, files) => {
  try {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await api.post(`/chats/${chatId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao fazer upload de arquivos:', error);
    throw error;
  }
};

export const atribuirAgente = async (chatId, agente) => {
  try {
    const response = await api.patch(`/chats/${chatId}/atribuir-agente`, null, {
      params: { agente }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao atribuir agente:', error);
    throw error;
  }
};

export const removerAgente = async (chatId, grupo = null) => {
  try {
    // Sempre enviar parâmetro grupo, mesmo que seja null ou string vazia
    const params = { grupo: grupo || '' };
    const response = await api.patch(`/chats/${chatId}/remover-agente`, null, {
      params
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao remover agente:', error);
    throw error;
  }
};

export const transferirChat = async (chatId, novoAgente) => {
  try {
    const response = await api.patch(`/chats/${chatId}/transferir`, null, {
      params: { novo_agente: novoAgente }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao transferir chat:', error);
    throw error;
  }
};

export const getUsuariosDisponiveis = async (minutes = 5) => {
  try {
    const response = await api.get(`/usuarios-disponiveis?minutes=${minutes}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar usuários:', error);
    throw error;
  }
};

export const createNewChatAPI = async (data) => {
  try {
    const response = await api.post('/chats/new', data);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar nova conversa:', error);
    throw error;
  }
};

// Grupos
export const getGroups = async () => api.get('/groups');
export const createGroup = async (name, description='') => api.post('/groups', { name, description });
export const deleteGroup = async (groupId) => api.delete(`/groups/${groupId}`);
export const setUserGroups = async (userId, groupIds, adminGroupIds = null) => {
  const payload = { group_ids: groupIds };
  if (adminGroupIds !== null && adminGroupIds !== undefined) {
    payload.admin_group_ids = adminGroupIds;
  }
  return api.patch(`/users/${userId}/groups`, payload);
};
export const getUserGroups = async (userId) => api.get(`/users/${userId}/groups`);

// Mensagens Pré-definidas
export const getMensagensPredefinidas = async (ativasApenas = false, categoria = null, busca = null) => {
  const params = new URLSearchParams();
  if (ativasApenas) params.append('ativas_apenas', 'true');
  if (categoria) params.append('categoria', categoria);
  if (busca) params.append('q', busca);
  return api.get(`/mensagens-predefinidas?${params.toString()}`);
};

export const getCategoriasMensagens = async () => api.get('/mensagens-predefinidas/categorias');

export const criarMensagemPredefinida = async (data) => api.post('/mensagens-predefinidas', data);

export const atualizarMensagemPredefinida = async (id, data) => api.put(`/mensagens-predefinidas/${id}`, data);

export const deletarMensagemPredefinida = async (id) => api.delete(`/mensagens-predefinidas/${id}`);

export const obterMensagemPredefinida = async (id) => api.get(`/mensagens-predefinidas/${id}`);

export const substituirVariaveisMensagem = async (id, variaveis) => 
  api.post(`/mensagens-predefinidas/${id}/substituir-variaveis`, variaveis);

export default api;
